import type { Grupo, ValorDeRespuesta } from './api.js';

/**
 * Lo que se lee al pasar de una parte del cuestionario a la siguiente.
 *
 * SON DOS TEXTOS Y NO UNO, escritos por la psicóloga en sitios distintos del
 * panel: el que CIERRA el bloque recién respondido (`retroalimentacion`) y el
 * que ABRE el que viene (`antesala` del grupo siguiente). Cualquiera de los dos
 * puede faltar; si faltan los dos no hay pantalla de paso y se entra directo.
 *
 * QUÉ TEXTO CIERRA UN BLOQUE:
 * - `ninguna` → nada
 * - `texto` → el fijo, si no está vacío
 * - `por_rangos` → promedio de las respuestas tipo escala del grupo; el primer
 *   rango cuyo `hasta` (inclusivo) cubre ese promedio. Sin ninguna escala
 *   respondida, o si el promedio queda por encima de todos los rangos → nada
 *   (la web se lo salta).
 * - `escrita_en_el_momento` → nada QUE SALGA DE AQUÍ. El texto no viene con el
 *   cuestionario: se pide aparte y llega segundos después, así que aquí vale
 *   `null` y la pantalla de paso se abre igual a esperarlo.
 */

export interface RespuestaParaFeedback {
  preguntaId: string;
  valor: ValorDeRespuesta;
}

/**
 * El texto que cierra un bloque, de los que ya vienen escritos.
 *
 * ES UN `switch` EXHAUSTIVO Y NO UNA ESCALERA DE `if`, y el motivo es un fallo
 * que estuvo a punto de llegar a producción: antes esto descartaba `ninguna`,
 * resolvía `texto` y DABA POR HECHO que lo que quedaba era `por_rangos`, leyendo
 * `retro.rangos` sin comprobarlo. Al añadir el cuarto modo, esa última rama
 * pasaba a recorrer `undefined` y reventaba el cuestionario en marcha, en mitad
 * de una sesión de alguien. Con el `switch`, el día que se añada un quinto modo
 * el `never` del final deja de compilar y el fallo aparece aquí y no allí.
 */
export function textoAlTerminarGrupo(
  grupo: Grupo,
  respuestas: readonly RespuestaParaFeedback[],
): string | null {
  const retro = grupo.retroalimentacion;

  switch (retro.modo) {
    case 'ninguna':
      return null;

    case 'escrita_en_el_momento':
      // El texto de este modo se pide aparte y no está aquí. Devolver `null` no
      // significa «no hay cierre», sino «todavía no»: quien pregunte por eso
      // tiene `elCierreSeEscribeAlMomento`.
      return null;

    case 'texto': {
      const texto = retro.texto.trim();
      return texto === '' ? null : texto;
    }

    case 'por_rangos':
      return textoDelRango(grupo, retro.rangos, respuestas);

    default: {
      // Inalcanzable mientras los cuatro modos estén cubiertos. Si aparece uno
      // nuevo, `retro` deja de ser `never` y esta línea no compila.
      const modoNoContemplado: never = retro;
      return modoNoContemplado;
    }
  }
}

/** El rango que le toca al promedio de las escalas de este grupo. */
function textoDelRango(
  grupo: Grupo,
  rangos: readonly { hasta: number; texto: string }[],
  respuestas: readonly RespuestaParaFeedback[],
): string | null {
  const idsDeEscala = new Set(
    grupo.preguntas.filter((pregunta) => pregunta.tipo === 'escala').map((pregunta) => pregunta.id),
  );

  const valores: number[] = [];
  for (const respuesta of respuestas) {
    if (!idsDeEscala.has(respuesta.preguntaId)) continue;
    if (respuesta.valor.tipo === 'escala') valores.push(respuesta.valor.valor);
  }

  if (valores.length === 0) return null;

  let suma = 0;
  for (const valor of valores) suma += valor;
  const promedio = suma / valores.length;

  for (const rango of rangos) {
    if (promedio <= rango.hasta) {
      const texto = rango.texto.trim();
      return texto === '' ? null : texto;
    }
  }

  return null;
}

/**
 * Si el cierre de esta parte hay que ir a buscarlo en vez de leerlo de aquí.
 *
 * ES LA ÚNICA PREGUNTA QUE HACE FALTA HACERSE SOBRE EL MODO NUEVO desde fuera de
 * este archivo, y por eso está aquí y no repartida en comparaciones sueltas por
 * la pantalla: el día que cambie el nombre del modo, cambia en un solo sitio.
 */
export function elCierreSeEscribeAlMomento(grupo: Grupo): boolean {
  return grupo.retroalimentacion.modo === 'escrita_en_el_momento';
}

export interface PantallaDePaso {
  /** Lo que cierra el bloque que se acaba de responder. */
  cierre: string | null;
  /** Lo que abre el bloque que viene. */
  antesala: string | null;
  /**
   * Si el cierre todavía viene en camino.
   *
   * SE MIRA ESTO Y NO `cierre === null` para saber si hay que esperar. Los dos
   * campos dicen cosas distintas: `cierre` en `null` con esto en `false` es «no
   * hay nada que leer y no lo habrá», y con esto en `true` es «todavía no ha
   * llegado». Confundirlos deja la pantalla yéndose sola justo antes de que
   * aparezca el texto, o esperando para siempre uno que nadie pidió.
   */
  cierrePorLlegar: boolean;
}

/**
 * Qué se lee entre dos partes, o `null` si no hay nada que leer.
 *
 * DEVUELVE `null` Y NO UN OBJETO CON LOS DOS CAMPOS VACÍOS, para que la pantalla
 * no tenga que decidir si «hay paso» mirando dos campos. Un cuestionario sin
 * ningún texto escrito —que es como está hoy en producción, a la espera de que
 * Ángela los redacte— pasa de una parte a otra igual que siempre.
 *
 * CON UN CIERRE QUE SE ESCRIBE EN EL MOMENTO SÍ HAY PANTALLA, aunque en este
 * instante no haya ni una letra que enseñar: el texto puede llegar un segundo
 * después, y para entonces la persona ya estaría respondiendo la parte
 * siguiente. Se abre a esperarlo, y si no llega se va sola.
 */
export function pantallaDePaso(
  termina: Grupo,
  empieza: Grupo,
  respuestas: readonly RespuestaParaFeedback[],
): PantallaDePaso | null {
  const cierre = textoAlTerminarGrupo(termina, respuestas);
  const antesalaEscrita = empieza.antesala?.trim() ?? '';
  const antesala = antesalaEscrita === '' ? null : antesalaEscrita;
  const cierrePorLlegar = elCierreSeEscribeAlMomento(termina);

  if (cierre === null && antesala === null && !cierrePorLlegar) return null;
  return { cierre, antesala, cierrePorLlegar };
}

/**
 * Cuánto se sostiene la pantalla de paso antes de irse sola.
 *
 * NO ES FIJO PORQUE EL TEXTO YA NO ES FIJO. Con un solo cierre de una línea
 * sobra tiempo; con un cierre más una antesala, el mismo tiempo corta la
 * lectura a la mitad y la sensación es la contraria a la que se busca. La
 * cuenta es tosca a propósito —tramos, no una fórmula por palabra—: el botón
 * «Continuar» está ahí para quien lea rápido.
 *
 * LOS TRAMOS SUBIERON 5 SEGUNDOS EN AGOSTO DE 2026 a pedido de la psicóloga,
 * tras probarlo con gente real: la pantalla se iba antes de terminar de leer
 * con calma. Quien lee rápido sigue teniendo el botón.
 *
 * VA EMPAREJADO CON LAS CLASES `.feedback--medio` y `.feedback--largo` DE
 * estilos.css, que son las que hacen durar lo mismo la barra de abajo. La
 * duración no se puede pasar por `style` en línea: la CSP del servidor es
 * `style-src 'self'` y el navegador lo bloquearía en producción.
 */
export type RitmoDePaso = 'corto' | 'medio' | 'largo';

export const DURACION_DEL_PASO_MS: Readonly<Record<RitmoDePaso, number>> = {
  corto: 10000,
  medio: 13000,
  largo: 17000,
};

/**
 * El tramo que le toca a lo que hay AHORA MISMO en la pantalla.
 *
 * SE MIDE CON EL TEXTO YA LLEGADO, NUNCA CON LA PANTALLA VACÍA. Mientras se
 * espera un cierre escrito en el momento no hay nada que contar, así que esto
 * diría «corto»; quien use el resultado para poner un temporizador se llevaría
 * la pantalla en cinco segundos y el texto llegaría a un sitio que ya no existe.
 * Por eso el temporizador no sale de aquí sino de `duracionDelPaso`, que sabe
 * cuándo todavía no toca contar.
 */
export function ritmoDelPaso(paso: PantallaDePaso): RitmoDePaso {
  const caracteres = (paso.cierre?.length ?? 0) + (paso.antesala?.length ?? 0);
  if (caracteres > 420) return 'largo';
  if (caracteres > 180) return 'medio';
  return 'corto';
}

/**
 * Cuánto se sostiene la pantalla antes de irse sola, o `null` si todavía no
 * puede empezar a contarse.
 *
 * SON TRES SITUACIONES Y NO DOS, y separarlas aquí es lo que evita que la
 * pantalla las confunda:
 * - `null` → el cierre viene en camino. NO SE ARRANCA NINGÚN TEMPORIZADOR: si
 *   corriera, la pantalla se iría antes de que llegue lo que se está esperando.
 * - `0` → se desistió de esperar y no queda nada que leer. La pantalla se va
 *   enseguida en vez de quedarse ahí en blanco esperando a que pase el tiempo de
 *   un texto que no existe.
 * - un tramo de `DURACION_DEL_PASO_MS` → hay algo que leer, y el tiempo se
 *   calcula sobre lo que se está leyendo de verdad.
 */
export function duracionDelPaso(paso: PantallaDePaso): number | null {
  if (paso.cierrePorLlegar) return null;
  if (paso.cierre === null && paso.antesala === null) return 0;
  return DURACION_DEL_PASO_MS[ritmoDelPaso(paso)];
}
